import { Routes, RouterModule } from '@angular/router';
import { NgModule } from '@angular/core';

import * as RoutesDefinitions from './routes-definitions';
import { AuthenticationLayoutComponent } from './authentication-module/layouts/authentication-layout/authentication-layout.component';
import { LoginPageComponent } from './authentication-module/pages/login-page/login-page.component';
import { LoginCallbackPageComponent } from './authentication-module/pages/login-callback-page/login-callback-page.component';
import { LogoutPageComponent } from './authentication-module/pages/logout-page/logout-page.component';
import { RegisterPageComponent } from './authentication-module/pages/register-page/register-page.component';
import { NewAccountLayoutComponent } from './account-module/layouts/new-account-layout/new-account-layout.component';
import { NewAccountCompanyPageComponent } from './account-module/pages/new-account-company-page/new-account-company-page.component';
import { NewAccountValidationPageComponent } from './account-module/pages/new-account-validation-page/new-account-validation-page.component';
import { SimpleLayoutComponent } from './shared-module/layouts/simple-layout/simple-layout.component';
import { SubsidiariesPageComponent } from './subsidiaries-module/pages/subsidiaries-page/subsidiaries-page.component';
import { AuthenticationGuard } from './authentication-module/guards';
import { NewAccountStepGuard } from './account-module/guards';
import { NewAccountStep } from './account-module/enums';
import { HasSubsidiariesGuard, SubsidiaryAdminGuard } from './subsidiaries-module/guards';
import { SubsidiaryDetailCompanyPageComponent } from './subsidiaries-module/pages/subsidiary-detail-company-page/subsidiary-detail-company-page.component';
import { SubsidiaryDetailUsersPageComponent } from './subsidiaries-module/pages/subsidiary-detail-users-page/subsidiary-detail-users-page.component';
import { SubsidiariesMenuLayoutComponent } from './subsidiaries-module/layouts/subsidiaries-menu-layout/subsidiaries-menu-layout.component';
import { NewFederationLayoutComponent } from './federations-module/layouts/new-federation-layout/new-federation-layout.component';
import { FederationFormPageComponent } from './federations-module/pages/federation-form-page/federation-form-page.component';
import { FederationValidationPageComponent } from './federations-module/pages/federation-validation-page/federation-validation-page.component';
import { FederationsMenuLayoutComponent } from './federations-module/layouts/federations-menu-layout/federations-menu-layout.component';
import { FederationDetailUsersPageComponent } from './federations-module/pages/federation-detail-users-page/federation-detail-users-page.component';


const routes: Routes = [
	// Authentication
	{
		path: RoutesDefinitions.AuthenticationRoutePath,
		component: AuthenticationLayoutComponent,
		children: [
			{
				path: RoutesDefinitions.LoginRoutePath,
				component: LoginPageComponent,
				data: { titleTranslationKey: 'Login', layoutName: 'login' }
			},
			{
				path: RoutesDefinitions.LoginCallbackRoutePath,
				component: LoginCallbackPageComponent,
				data: { titleTranslationKey: 'Login', layoutName: 'login' }
			},
			{
				path: RoutesDefinitions.LoginFromSignupCallbackRoutePath,
				component: LoginCallbackPageComponent,
				data: { fromSignup: true, titleTranslationKey: 'Login', layoutName: 'login' }
			},
			{
				path: RoutesDefinitions.LoginFromInviteCallbackRoutePath,
				component: LoginCallbackPageComponent,
				data: { fromInvite: true, titleTranslationKey: 'Login', layoutName: 'login' }
			},
			{
				path: RoutesDefinitions.LogoutRoutePath,
				component: LogoutPageComponent,
				data: { titleTranslationKey: 'Logout', layoutName: 'logout' }
			},
			{
				path: RoutesDefinitions.RegisterRoutePath,
				component: RegisterPageComponent,
				data: { titleTranslationKey: 'Register', layoutName: 'register' }
			},
		]
	},

	// New account
	{
		path: RoutesDefinitions.NewAccountRoutePath,
		component: NewAccountLayoutComponent,
		canActivate: [AuthenticationGuard],
		children: [
			{
				path: RoutesDefinitions.NewAccountCompanyRoutePath,
				component: NewAccountCompanyPageComponent,
				canActivate: [NewAccountStepGuard],
				data: { titleTranslationKey: 'NewAccountCompany', layoutName: 'new-account', step: NewAccountStep.Company }
			},
			{
				path: RoutesDefinitions.NewAccountValidationRoutePath,
				component: NewAccountValidationPageComponent,
				canActivate: [NewAccountStepGuard],
				data: { titleTranslationKey: 'NewAccountValidation', layoutName: 'new-account', step: NewAccountStep.Validation, showTenant: true }
			},
			{
				path: RoutesDefinitions.NewAccountValidationRoutePathWithId,
				component: NewAccountValidationPageComponent,
				canActivate: [NewAccountStepGuard],
				data: { titleTranslationKey: 'NewAccountValidation', layoutName: 'new-account', step: NewAccountStep.Validation, showTenant: true }
			}
		]
	},

	// Subsidiaries
	{
		path: RoutesDefinitions.SubsidiariesRoutePath,
		component: SimpleLayoutComponent,
		canActivate: [AuthenticationGuard],
		children: [
			{
				path: '',
				pathMatch: 'full',
				component: SubsidiariesPageComponent,
				data: { titleTranslationKey: 'Subsidiaries', layoutName: 'subsidiaries' }
			},
		]
	},

	// Subsidiary detail
	{
		path: `${RoutesDefinitions.SubsidiariesRoutePath}/${RoutesDefinitions.SubsidiaryDetailRoutePath}`,
		component: SubsidiariesMenuLayoutComponent,
		canActivate: [AuthenticationGuard, HasSubsidiariesGuard, SubsidiaryAdminGuard],
		data: { showTenant: true },
		children: [
			{
				path: RoutesDefinitions.SubsidiaryDetailCompanyRoutePath,
				component: SubsidiaryDetailCompanyPageComponent,
				data: { titleTranslationKey: 'SubsidiaryDetailCompany', layoutName: 'subsidiary-detail' }
			},
			{
				path: RoutesDefinitions.SubsidiaryDetailUsersRoutePath,
				component: SubsidiaryDetailUsersPageComponent,
				data: { titleTranslationKey: 'SubsidiaryDetailUsers', layoutName: 'subsidiary-detail' }
			},

			{ path: '**', redirectTo: RoutesDefinitions.SubsidiaryDetailCompanyRoutePath }
		]
	},

	// Federations
	{
		path: RoutesDefinitions.FederationsRoutePath,
		component: SimpleLayoutComponent,
		canActivate: [AuthenticationGuard],
		children: [
			{
				path: '',
				pathMatch: 'full',
				component: SubsidiariesPageComponent,
				data: { titleTranslationKey: 'Federations', layoutName: 'federations' }
			},
		]
	},

	// Federations
	{
		path: RoutesDefinitions.FederationsRoutePath,
		component: NewFederationLayoutComponent,
		canActivate: [AuthenticationGuard],
		children: [
			{
				path: RoutesDefinitions.FederationCreateRoutePath,
				children: [
					{
						path: '',
						pathMatch: 'full',
						component: FederationFormPageComponent,
						data: { titleTranslationKey: 'FederationCreate', layoutName: 'federation-create' },
					},
					{
						path: RoutesDefinitions.FederationValidationRoutePath,
						pathMatch: 'full',
						component: FederationValidationPageComponent,
						data: { titleTranslationKey: 'NewFederationValidation', layoutName: 'federation-validation' },
					}
				]
			},
		],
	},

	// Federation Detail
	{
		path: `${RoutesDefinitions.FederationsRoutePath}/${RoutesDefinitions.FederationDetailRoutePath}`,
		component: FederationsMenuLayoutComponent,
		canActivate: [AuthenticationGuard],
		children: [
			{
				path: '',
				component: FederationFormPageComponent,
				data: { titleTranslationKey: 'FederationCreate', layoutName: 'federation-detail' }
			},
			{
				path: RoutesDefinitions.FederationDetailUsersRoutePath,
				component: FederationDetailUsersPageComponent,
				data: { titleTranslationKey: 'FederationDetailUsers', layoutName: 'federation-detail' }
			},

			{ path: '**', redirectTo: RoutesDefinitions.FederationDetailRoutePath }
		]
	},

	// Redirects to login page
	{ path: '', pathMatch: 'prefix', redirectTo: RoutesDefinitions.getSubsidiariesFullPath() }
];

@NgModule({
	imports: [RouterModule.forRoot(routes)],
	exports: [RouterModule]
})
// eslint-disable-next-line @typescript-eslint/no-extraneous-class
export class AppRoutingModule { }
